import React from 'react';
import { useState, useEffect } from 'react';
import Welcome from './Components/Welcome';
import OrganizationForm from './Components/OrganizationForm';
import AssessmentSection from './Components/AssessmentSection';
import Results from './Components/Results';
import ProgressBar from './Components/ProgressBar';
import { useAssessmentStore } from './store/assessmentStore';
import GoogleAnalytics from './components/GoogleAnalytics';
import { calculateResults } from './utils/scoring';

type Step = 'welcome' | 'assessment' | 'organization' | 'results';

function App() {
  const [step, setStep] = useState<Step>('welcome');
  const [currentCategory, setCurrentCategory] = useState(0);
  const assessmentState = useAssessmentStore();

  useEffect(() => {
    // Only clear state if BOTH results AND organization data are missing
    if (!assessmentState.results || !assessmentState.organizationData) {
      assessmentState.clearAssessment();
    }
    setStep('welcome');
  }, []);

  // Debug logging for step changes
  useEffect(() => {
    console.log('Step changed:', {
      step,
      category: currentCategory,
      isComplete: assessmentState.isComplete,
      hasResults: !!assessmentState.results,
      hasOrgData: !!assessmentState.organizationData
    });
  }, [step, currentCategory, assessmentState.isComplete, assessmentState.results, assessmentState.organizationData]);
  
  const handleOrganizationSubmit = () => {
    setStep('results');
  };

  return (
    <div className="min-h-screen">
      <GoogleAnalytics />
      {/* Header */}
      <header className="bg-[#FCF5EB] shadow-sm fixed top-0 left-0 right-0 z-50">
        <div className="px-8 h-16 flex items-center justify-between">
          <a 
            href="https://www.shepherdops.com" 
            className="flex items-center text-gray-900 hover:opacity-90 transition-opacity"
          >
            <img 
              src="https://nidnapwlxvhavlhyfrlx.supabase.co/storage/v1/object/public/assets//shepherdops.png" 
              alt="ShepherdOps Logo" 
              className="h-8 w-auto object-contain"
              crossOrigin="anonymous"
              onError={(e) => {
                console.error('Logo failed to load:', e);
                const img = e.target as HTMLImageElement;
                img.onerror = null; // Prevent infinite loop
                img.src = '/favicon.svg'; // Fallback to favicon
              }}
            />
          </a>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative pt-24 pb-16">
        {/* Background Image with Overlay */}
        <div className="fixed inset-0 z-0 bg-[#163B56]" />

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Main Heading */}
          <div className="text-center mb-8 relative z-10">
            <h1 className="text-4xl md:text-5xl font-bold text-white">
              Church Operations Assessment
            </h1>
            <p className="mt-4 text-xl md:text-2xl text-white/90">
              Evaluate and improve your church's operational effectiveness
            </p>
          </div>

          <div className="relative z-10">
            <ProgressBar step={step} currentCategory={currentCategory} />
          
            <div className="mt-6 bg-[#FCF5EB] rounded-lg shadow-lg p-8">
            {step === 'welcome' && (
              <Welcome onStart={() => setStep('assessment')} />
            )}
            
            {step === 'assessment' && (
              <AssessmentSection
                category={currentCategory}
                responses={assessmentState.currentResponses}
                onResponse={assessmentState.setResponse}
                onNext={() => {
                  if (currentCategory === 4) {
                    // Calculate and set results before moving to organization step
                    const results = calculateResults(assessmentState.currentResponses);
                    if (results) {
                      assessmentState.setResults(results);
                      setStep('organization');
                    } else {
                      console.error('Failed to calculate assessment results');
                    }
                  } else {
                    setCurrentCategory(prev => prev + 1);
                  }
                }}
                onBack={() => {
                  if (currentCategory === 0) {
                    setStep('welcome');
                  } else {
                    setCurrentCategory(prev => prev - 1);
                  }
                }}
              />
            )}
            
            {step === 'organization' && (
              <OrganizationForm 
                onSubmit={handleOrganizationSubmit}
                onBack={() => {
                  setCurrentCategory(4);
                  setStep('assessment');
                }}
              />
            )}
            
            {step === 'results' && assessmentState.results && assessmentState.organizationData && (
              <Results 
                results={assessmentState.results}
                organizationData={assessmentState.organizationData}
                onRestart={() => {
                  assessmentState.clearAssessment();
                  setStep('welcome');
                  setCurrentCategory(0);
                }} 
              />
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white shadow-sm mt-auto">
        <div className="max-w-4xl mx-auto px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-gray-600 text-sm">
            © {new Date().getFullYear()} ShepherdOps. All rights reserved.
          </div>
          <a 
            href="https://www.shepherdops.com"
            className="text-sm text-[#163B56] hover:text-[#0D2436] transition-colors"
          >
            Visit ShepherdOps
          </a>
        </div>
      </footer>
    </div>
  );
}

export default App;